import React from 'react';
import { motion } from 'framer-motion';
import type { Site } from '../types';

interface IncidentsListProps {
    incidents: Site[];
}

const statusIcons = {
    down: (
        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
    ),
    warn: (
        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
        </svg>
    ),
    up: null
};

const formatLocalDateTime = (timestamp: number): string => {
    if (!timestamp) return '—';
    
    return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
    }).format(new Date(timestamp));
};

const formatCheckedAgo = (timestamp: number): string => {
    if (!timestamp || isNaN(timestamp)) return '—';
    
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 5) return 'только что';
    if (seconds < 60) return `${seconds} сек`;

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} мин`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} ч ${minutes % 60} мин`;

    const days = Math.floor(hours / 24);
    return `${days} д ${hours % 24} ч`;
};

export const IncidentsList: React.FC<IncidentsListProps> = ({ incidents }) => {
    if (incidents.length === 0) return null;

    return (
        <div className="mt-6">
            <div className="flex items-center justify-between gap-3 px-[18px] mb-2.5">
                <h2 className="text-[13px] font-medium text-[var(--text)]">
                    Активные проблемы ({incidents.length})
                </h2>
            </div>

            <div className="flex flex-col gap-2 px-[18px]">
                {incidents.map((site, index) => {
                    const isDown = site.status === 'down';
                    const fallTime = site.problemSince || site.lastCheck;
                    
                    return (
                        <motion.div
                            key={site.id}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            whileHover={{ x: 4 }}
                            className="flex items-start gap-3 p-3.5 bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl cursor-pointer hover:border-[var(--border-strong)] transition-all"
                        >
                            <div className={`
                                w-8 h-8 rounded-lg flex items-center justify-center shrink-0
                                ${isDown ? 'bg-[var(--status-down-bg)] text-[var(--status-down)]' : 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]'}
                            `}>
                                {statusIcons[site.status]}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-medium text-[var(--text)] mb-0.5 truncate">
                                    {site.name} {isDown ? 'недоступен' : 'проблемы'}
                                </div>
                                <div className="text-[11px] text-[var(--text-muted)]">
                                    <a 
                                        href={site.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:text-[var(--brand)] transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {site.url.replace('https://', '')}
                                    </a>
                                </div>
                                <div className="text-[11px] text-[var(--status-down)] mt-1">
                                    Упал: {formatLocalDateTime(fallTime)} ({formatCheckedAgo(fallTime)})
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
};